/**
 * Custom cursor image + optional color scheme swap based on section visibility and scroll direction.
 */

/**
 * Section top vs viewport (scroll-down activation uses top; scroll-up revert uses bottom gap).
 * @param {DOMRect} rect
 * @param {number} vh
 */
function getSectionTopViewportMetrics(rect, vh) {
  if (vh <= 0) {
    return {
      topFromViewportTopPx: rect.top,
      topFromViewportTopNorm: 0,
      gapTopToViewportBottomPx: 0,
      gapTopToViewportBottomNorm: 0,
    };
  }

  return {
    /** px from viewport top to section top */
    topFromViewportTopPx: rect.top,
    /** rect.top / vh — 0 at top edge, 1 when top meets viewport bottom */
    topFromViewportTopNorm: rect.top / vh,
    /** px from section top to viewport bottom */
    gapTopToViewportBottomPx: vh - rect.top,
    /** (vh - rect.top) / vh — small when section top sits near viewport bottom */
    gapTopToViewportBottomNorm: (vh - rect.top) / vh,
  };
}

class CustomCursorSection extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {(() => void) | null} */
  #onScroll = null;

  /** @type {(() => void) | null} */
  #onResize = null;

  /** @type {HTMLDivElement | null} */
  #follower = null;

  /** @type {HTMLElement | null} */
  #sectionEl = null;

  /** @type {HTMLElement | null} */
  #backgroundEl = null;

  /** @type {((e: MouseEvent) => void) | null} */
  #onWindowMouseMove = null;

  /** @type {boolean} */
  #pointerInsideSection = false;

  /** @type {number} */
  #lastScrollY = 0;

  /** @type {number} */
  #scrollDelta = 0;

  /** @type {boolean} */
  #lockSecond = false;

  /** @type {number} */
  #intersectionThreshold = 0.5;

  /** @type {number} */
  #revertVisibilityThreshold = 0.2;

  /** @type {boolean} */
  #maintainSecondOnScroll = false;

  /** @type {number | null} */
  #stabilizeScrollRaf = null;

  /** @type {boolean} */
  #scrollStabilized = false;

  connectedCallback() {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reducedMotion.matches) {
      return;
    }

    this.#backgroundEl = this.querySelector('.section-background');
    this.#sectionEl = this.querySelector('.section');

    const enableScroll = this.dataset.enableScrollColorScheme === 'true';
    const baseScheme = this.dataset.baseColorScheme || '';
    const scrollScheme = this.dataset.scrollColorScheme || '';
    const cursorUrl = (this.dataset.cursorImageUrl || '').trim();

    const rawPct = parseInt(this.dataset.intersectionThresholdPercent || '50', 10);
    const pct = Number.isFinite(rawPct) ? Math.min(100, Math.max(0, rawPct)) : 50;
    this.#intersectionThreshold = pct / 100;

    this.#maintainSecondOnScroll = this.dataset.maintainSecondOnScroll === 'true';

    if (this.#maintainSecondOnScroll) {
      const rawRevert = parseInt(this.dataset.revertVisibilityPercent || '20', 10);
      let revert = Number.isFinite(rawRevert) ? Math.min(100, Math.max(0, rawRevert)) / 100 : 0.2;
      if (revert > this.#intersectionThreshold) {
        revert = Math.max(0, this.#intersectionThreshold - 0.01);
      }
      this.#revertVisibilityThreshold = revert;
    }

    if (enableScroll && baseScheme && scrollScheme && this.#sectionEl) {
      this.#lastScrollY = window.scrollY;
      this.#scrollStabilized = false;

      const thresholds = Array.from({ length: 101 }, (_, i) => i / 100);

      this.#intersectionObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[entries.length - 1];
          if (!entry) {
            return;
          }
          if (this.#maintainSecondOnScroll) {
            this.#applyMaintainScrollScheme(baseScheme, scrollScheme);
          } else {
            const active = entry.intersectionRatio >= this.#intersectionThreshold ? scrollScheme : baseScheme;
            this.#applyColorScheme(active);
          }
        },
        { threshold: thresholds }
      );
      this.#intersectionObserver.observe(this.#sectionEl);

      if (this.#maintainSecondOnScroll) {
        this.#onScroll = () => {
          const y = window.scrollY;
          this.#scrollDelta = y - this.#lastScrollY;
          this.#lastScrollY = y;
          this.#applyMaintainScrollScheme(baseScheme, scrollScheme);
        };
        this.#onResize = () => {
          this.#scrollDelta = 0;
          this.#applyMaintainScrollScheme(baseScheme, scrollScheme);
        };
        window.addEventListener('scroll', this.#onScroll, { passive: true });
        window.addEventListener('resize', this.#onResize);

        // Absorb scroll restoration / late layout so the first scroll delta is not
        // (restoredY - 0) → false activate-lock + locked-scroll-down on load.
        this.#stabilizeScrollRaf = requestAnimationFrame(() => {
          this.#stabilizeScrollRaf = requestAnimationFrame(() => {
            this.#stabilizeScrollRaf = null;
            this.#lastScrollY = window.scrollY;
            this.#scrollDelta = 0;
            this.#scrollStabilized = true;
          });
        });
      }
    }

    if (cursorUrl && this.#sectionEl) {
      this.#setupCursorFollower(cursorUrl);
    }
  }

  disconnectedCallback() {
    if (this.#stabilizeScrollRaf !== null) {
      cancelAnimationFrame(this.#stabilizeScrollRaf);
      this.#stabilizeScrollRaf = null;
    }

    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;

    if (this.#onScroll) {
      window.removeEventListener('scroll', this.#onScroll);
      this.#onScroll = null;
    }
    if (this.#onResize) {
      window.removeEventListener('resize', this.#onResize);
      this.#onResize = null;
    }

    if (this.#follower?.isConnected) {
      this.#follower.remove();
    }
    this.#follower = null;

    if (this.#onWindowMouseMove) {
      window.removeEventListener('mousemove', this.#onWindowMouseMove);
      this.#onWindowMouseMove = null;
    }

    if (this.#sectionEl) {
      this.#sectionEl.style.cursor = '';
    }

    this.#sectionEl = null;
    this.#backgroundEl = null;
  }

  /**
   * @param {string} baseScheme
   * @param {string} scrollScheme
   */
  #applyMaintainScrollScheme(baseScheme, scrollScheme) {
    const section = this.#sectionEl;
    if (!section) {
      return;
    }

    const rect = section.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const metrics = getSectionTopViewportMetrics(rect, vh);

    /** Activation: section top reaches upper (t × 100)% of viewport — rect.top / vh <= 1 - t */
    const tActivate = this.#intersectionThreshold;
    /** Revert: gap from section top to viewport bottom is under (t × 100)% of viewport height */
    const tRevert = this.#revertVisibilityThreshold;
    const activateLinePx = vh * (1 - tActivate);

    const delta = this.#scrollDelta;
    this.#scrollDelta = 0;

    const scrolledDown = delta > 0;
    const scrolledUp = delta < 0;

    const pastActivateFromTop = rect.top <= activateLinePx;
    /** Below fold: section top still under viewport bottom — no bottom-gap revert */
    const sectionTopAboveViewportBottom = rect.top < vh;
    const shouldRevertFromBottom =
      sectionTopAboveViewportBottom && metrics.gapTopToViewportBottomNorm < tRevert;

    /** @type {string} */
    let active = baseScheme;

    if (this.#lockSecond) {
      if (scrolledDown) {
        active = scrollScheme;
      } else if (scrolledUp && shouldRevertFromBottom) {
        this.#lockSecond = false;
        active = baseScheme;
      } else {
        active = scrollScheme;
      }
    } else if (this.#scrollStabilized && scrolledDown && pastActivateFromTop) {
      this.#lockSecond = true;
      active = scrollScheme;
    } else {
      active = baseScheme;
    }

    this.#applyColorScheme(active);
  }

  /**
   * @param {string} schemeId
   */
  #applyColorScheme(schemeId) {
    if (!schemeId) {
      return;
    }
    const nextClass = `color-${schemeId}`;

    for (const el of [this.#backgroundEl, this.#sectionEl]) {
      if (!el) {
        continue;
      }
      for (const c of [...el.classList]) {
        if (c.startsWith('color-')) {
          el.classList.remove(c);
        }
      }
      el.classList.add(nextClass);
    }
  }

  /**
   * @param {string} cursorUrl
   */
  #setupCursorFollower(cursorUrl) {
    const section = this.#sectionEl;
    if (!section) {
      return;
    }

    const follower = document.createElement('div');
    follower.className = 'custom-cursor-section__follower';
    follower.setAttribute('aria-hidden', 'true');
    Object.assign(follower.style, {
      position: 'fixed',
      left: '0',
      top: '0',
      width: '0',
      height: '0',
      pointerEvents: 'none',
      zIndex: '99999',
      opacity: '0',
      transition: 'opacity 0.15s ease-out',
    });

    const img = document.createElement('img');
    img.src = cursorUrl;
    img.alt = '';
    img.setAttribute('role', 'presentation');
    img.decoding = 'async';
    Object.assign(img.style, {
      position: 'absolute',
      left: '0',
      top: '0',
      transform: 'translate(-50%, -50%)',
      maxWidth: '128px',
      maxHeight: '128px',
      width: 'auto',
      height: 'auto',
    });

    follower.appendChild(img);
    document.body.appendChild(follower);
    this.#follower = follower;

    this.#onWindowMouseMove = (e) => {
      if (!this.#pointerInsideSection || !this.#follower) {
        return;
      }
      this.#follower.style.left = `${e.clientX}px`;
      this.#follower.style.top = `${e.clientY}px`;
    };

    window.addEventListener('mousemove', this.#onWindowMouseMove);

    section.addEventListener('mouseenter', () => {
      this.#pointerInsideSection = true;
      section.style.cursor = 'none';
      if (this.#follower) {
        this.#follower.style.opacity = '1';
      }
    });

    section.addEventListener('mouseleave', () => {
      this.#pointerInsideSection = false;
      section.style.cursor = '';
      if (this.#follower) {
        this.#follower.style.opacity = '0';
      }
    });
  }
}

if (!customElements.get('custom-cursor-section')) {
  customElements.define('custom-cursor-section', CustomCursorSection);
}
